import { useEffect, useRef, useState } from 'react'
import { RepoProviderIcon } from '@/shared/components/RepoProviderIcon'
import { Tooltip } from '@/shared/components/Tooltip'
import { SeverityDonut } from '@/shared/components/SeverityDonut'
import { IconButton } from '@/shared/components/IconButton'
import { ServeOverrideControl, ServeOverrideBadge, serveOverrideToast } from '@/shared/components/ServeOverrideControl'
import { CodeIcon, FileDownIcon, ExternalLinkIcon } from '@/shared/components/Icons'
import { useToast } from '@/context/ToastContext'
import type { ProviderArtefactVersion, ServeOverrideAction } from '@/lib/apiTypes'
import { downloadProviderSbom, setProviderVersionOverride } from '@/lib/hooks'
import { formatDate, formatBytes as formatBytesUtil } from '@/lib/format'
import { releaseUrl } from '@/features/providers/lib/urls'
import { repoHost } from '@/features/providers/lib/versions'
import { VersionExpansion, buildBadgeTip, type VersionExpansionTab } from '@/features/providers/tabs/version/VersionExpansion'

export function VersionRow({ orgId, upstream, namespace, type, sourceUrl, v, canAdmin, expanded, onToggleExpand, onExpand, onConsume, onChanged }: {
  orgId: string
  upstream: string
  namespace: string
  type: string
  sourceUrl: string | null
  v: ProviderArtefactVersion
  canAdmin: boolean
  expanded: boolean
  onToggleExpand: () => void
  /** Open (never collapse) this row's expansion - used by the tab-targeting cells. */
  onExpand: () => void
  onConsume: () => void
  onChanged: () => void
}) {
  const { showToast } = useToast()
  const [sbomBusy, setSbomBusy] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  // The expansion is a tabbed panel (vulnerabilities / module references / live usage) - the
  // row expands whenever ANY tab has content, not just when there are findings.
  const expandable = v.findings.length > 0 || v.used_by.length > 0 || v.referenced_by_modules.length > 0 || !!v.build
  // A version a workspace is running RIGHT NOW that carries a critical: the row an infra
  // engineer needs to see first. Flagged with a red rule and the usage tab opens by default.
  const inUseWithCritical = v.usage_workspace_count > 0 && v.critical_count > 0
  // null = "not chosen yet" → default to the first tab with content, vulnerabilities first
  // (usage first when the version is in use with a critical, so the blast radius is immediate).
  const [tab, setTab] = useState<VersionExpansionTab | null>(null)
  const defaultTab: VersionExpansionTab =
    inUseWithCritical ? 'usage' : v.findings.length > 0 || v.build ? 'vulns' : v.used_by.length > 0 ? 'usage' : 'refs'
  const activeTab = tab ?? defaultTab
  function openTab(t: VersionExpansionTab) {
    setTab(t)
    if (!expanded) onExpand()
  }
  const relUrl = releaseUrl(sourceUrl, v.version)
  const expansionRef = useRef<HTMLTableRowElement | null>(null)

  // Bring a freshly opened expansion into view WITHOUT the page/modal jumping: scroll only the
  // table's own scroll container (scrollIntoView walks every scrollable ancestor - that jumped
  // the whole modal), only downward, and never so far that the version row scrolls out of view
  // under the sticky header.
  useEffect(() => {
    if (!expanded) return
    const rowEl = expansionRef.current
    const container = rowEl?.closest('.table-scroll')
    if (!rowEl || !(container instanceof HTMLElement)) return
    const rowRect = rowEl.getBoundingClientRect()
    const contRect = container.getBoundingClientRect()
    const overflow = rowRect.bottom - contRect.bottom
    if (overflow > 0) {
      // 68px keeps the sticky header + the owning version row visible above the expansion.
      container.scrollTop += Math.min(overflow, Math.max(0, rowRect.top - contRect.top - 68))
    }
  }, [expanded])

  async function downloadSbom() {
    setSbomBusy(true)
    try {
      await downloadProviderSbom(orgId, upstream, namespace, type, v.version)
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setSbomBusy(false)
    }
  }

  async function applyOverride(action: ServeOverrideAction, reason?: string) {
    setBusy('override')
    try {
      await setProviderVersionOverride(orgId, upstream, namespace, type, v.version, action, reason)
      showToast(serveOverrideToast(`v${v.version}`, action, 'org'))
      onChanged()
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
    {/* Whole row toggles the findings expansion (row-link cursor/hover); interactive cells
        stopPropagation so links/buttons don't double as a toggle. */}
    <tr
      className={expandable ? 'row-link' : undefined}
      style={inUseWithCritical ? { boxShadow: 'inset 3px 0 0 var(--red)' } : undefined}
      title={inUseWithCritical ? `In use by ${v.usage_workspace_count} workspace${v.usage_workspace_count === 1 ? '' : 's'} with ${v.critical_count} critical finding${v.critical_count === 1 ? '' : 's'}` : undefined}
      role={expandable ? 'button' : undefined}
      tabIndex={expandable ? 0 : undefined}
      onClick={expandable ? onToggleExpand : undefined}
      onKeyDown={expandable ? (e) => {
        // Row-level Enter/Space toggle, but only when focus is on the row itself - the inner
        // chevron button and action cells handle their own keys.
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault()
          onToggleExpand()
        }
      } : undefined}
      aria-expanded={expandable ? expanded : undefined}
    >
      <td style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          {/* Chevron INSIDE the version cell (same idiom as the old by-version panel) - the table
              is already at horizontal-scroll pressure. The row itself also toggles; the button
              stays for keyboard/screen-reader access and stops propagation to avoid a double
              toggle. */}
          {expandable && (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} details for v${v.version}`}
              onClick={(e) => { e.stopPropagation(); onToggleExpand() }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text3)', fontSize: '0.7rem', width: 14 }}
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
          v{v.version}
          <ServeOverrideBadge value={v.serve_override} reason={v.serve_override_reason} />
        </span>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {relUrl ? (
          <Tooltip text={`Open the v${v.version} release page on ${repoHost(sourceUrl) === 'github' ? 'GitHub' : 'GitLab'}`} placement="top">
            <a
              href={relUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open the v${v.version} release page`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'inherit' }}
            >
              <RepoProviderIcon provider={repoHost(sourceUrl)} />
              <span style={{ color: 'var(--text3)', display: 'inline-flex' }}><ExternalLinkIcon size={11} /></span>
            </a>
          </Tooltip>
        ) : (
          <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>—</span>
        )}
      </td>
      <td style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>
        {v.released_at != null ? formatDate(v.released_at) : <span className="dim">—</span>}
      </td>
      <td>
        {v.build ? (
          <Tooltip text={buildBadgeTip(v.build, v.version)} placement="top">
            <button
              className="btn btn-ghost btn-sm"
              style={{ padding: 0, border: 'none', background: 'none' }}
              onClick={(e) => { e.stopPropagation(); openTab('vulns') }}
              aria-label={`Show the terramantle build ${v.build.full_version} for v${v.version}`}
            >
              <code className="mono" style={{ fontSize: '0.78rem' }}>{v.build.full_version}</code>
            </button>
          </Tooltip>
        ) : (
          <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>—</span>
        )}
      </td>
      <td>
        {v.usage_workspace_count > 0 ? (
          <Tooltip text="Which state workspaces lock this exact version - opens the Live usage tab below" placement="top">
            <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); openTab('usage') }}>
              {v.usage_workspace_count} ws
            </button>
          </Tooltip>
        ) : (
          <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>—</span>
        )}
      </td>
      <td>
        {v.referenced_module_count > 0 ? (
          <Tooltip text="Which modules declare this provider at a constraint admitting this version - opens the Module references tab below" placement="top">
            <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); openTab('refs') }}>
              {v.referenced_module_count} mod{v.referenced_module_count === 1 ? '' : 's'}
            </button>
          </Tooltip>
        ) : (
          <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>—</span>
        )}
      </td>
      <td>
        {!v.scanned ? (
          // Listed because a workspace locks / a module references it, before any scan completed.
          // Must never read as "clean": say so explicitly.
          <Tooltip text="No completed scan for this version yet - it is listed because a workspace locks it or a module references it. Zero findings here means no evidence, not a clean result." placement="top">
            <span tabIndex={0} style={{ fontSize: '0.72rem', color: 'var(--text3)', fontStyle: 'italic', cursor: 'help' }}>not scanned</span>
          </Tooltip>
        ) : v.findings.length > 0 ? (
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: '0.75rem', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            aria-expanded={expanded}
            aria-label={`${v.findings.length} finding${v.findings.length === 1 ? '' : 's'}: ${v.critical_count} critical, ${v.high_count} high`}
            onClick={(e) => { e.stopPropagation(); if (expanded && activeTab === 'vulns') onToggleExpand(); else openTab('vulns') }}
          >
            <SeverityDonut critical={v.critical_count} high={v.high_count} total={v.vuln_count} unfixableHigh={v.has_unfixable_high} maxCvss={v.max_cvss} focusable={false} />
            <span aria-hidden>{expanded && activeTab === 'vulns' ? '▾' : '▸'}</span>
          </button>
        ) : (
          <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>—</span>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          {v.has_sbom && (
            <IconButton
              label={`Download SBOM for v${v.version}`}
              tip={`Download the SBOM${v.sbom_size != null ? ` (${formatBytesUtil(v.sbom_size)})` : ''} - the software bill of materials from the last completed scan.`}
              busy={sbomBusy}
              onClick={downloadSbom}
            >
              <FileDownIcon />
            </IconButton>
          )}
          <IconButton
            label={`Consume v${v.version}`}
            tip={`Consume config pinned to v${v.version} - required_providers block, your registry endpoint and CLI steps.`}
            onClick={onConsume}
          >
            <CodeIcon />
          </IconButton>
          {canAdmin && (
            <>
              <span aria-hidden style={{ width: 1, height: 16, background: 'var(--border)' }} />
              <ServeOverrideControl
                value={v.serve_override}
                subject={`v${v.version}`}
                busy={busy === 'override'}
                compact
                onApply={applyOverride}
              />
            </>
          )}
        </span>
      </td>
    </tr>
    {expanded && (
      <tr ref={expansionRef}>
        <td colSpan={8} style={{ background: 'var(--bg2)', padding: '10px 14px 14px' }}>
          <VersionExpansion orgId={orgId} upstream={upstream} namespace={namespace} type={type} v={v} tab={activeTab} onTab={setTab} />
        </td>
      </tr>
    )}
    </>
  )
}
